import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from './config';
import './SharedInfo.css';

function renderSimpleMarkdown(text) {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    return (
      <p key={i} className="shared-info-desc-line">
        {parts.map((part, j) =>
          part.startsWith('**') && part.endsWith('**') ? (
            <strong key={j}>{part.slice(2, -2)}</strong>
          ) : (
            <span key={j}>{part}</span>
          )
        )}
      </p>
    );
  });
}

const emptyForm = () => ({
  title: '',
  description: '',
  url: 'https://',
  category: '',
  newCategoryName: '',
  pinned: false,
});

function SharedInfo({ onAttentionChange }) {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const loadCategories = useCallback(async () => {
    const res = await axios.get(`${API_URL}/portfolio/info-categories/?page_size=all`);
    const data = res.data.results || res.data;
    setCategories(Array.isArray(data) ? data : []);
  }, []);

  const loadItems = useCallback(async () => {
    const params = { page_size: 'all' };
    if (showArchived) params.include_archived = 'true';
    if (filterCategory === '__none__') params.category = '__none__';
    else if (filterCategory) params.category = filterCategory;
    const res = await axios.get(`${API_URL}/portfolio/info-items/`, { params });
    const data = res.data.results || res.data;
    setItems(Array.isArray(data) ? data : []);
  }, [showArchived, filterCategory]);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await Promise.all([loadCategories(), loadItems()]);
    } catch (e) {
      setError(
        e.response?.data?.detail ||
          (typeof e.response?.data === 'string' ? e.response.data : null) ||
          e.message
      );
    } finally {
      setLoading(false);
    }
  }, [loadCategories, loadItems]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const notifyAttention = useCallback(() => {
    if (typeof onAttentionChange === 'function') {
      onAttentionChange();
    }
  }, [onAttentionChange]);

  const patchSeen = async (item, seen) => {
    try {
      await axios.patch(`${API_URL}/portfolio/info-items/${item.id}/my-state/`, { seen });
      await loadItems();
      notifyAttention();
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || e.message);
    }
  };

  const openAddModal = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormError(null);
    setModalOpen(true);
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setFormError(null);
    setForm({
      title: item.title || '',
      description: item.description || '',
      url: item.url || 'https://',
      category: item.category != null ? String(item.category) : '',
      newCategoryName: '',
      pinned: !!item.pinned,
    });
    setModalOpen(true);
  };

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingId(null);
    setForm(emptyForm());
    setFormError(null);
  }, []);

  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalOpen, closeModal]);

  const resolveCategoryId = async () => {
    const newName = (form.newCategoryName || '').trim();
    if (newName) {
      const res = await axios.post(`${API_URL}/portfolio/info-categories/`, { name: newName });
      await loadCategories();
      return res.data.id;
    }
    const c = form.category;
    if (c === '' || c == null) return null;
    const id = parseInt(c, 10);
    return Number.isNaN(id) ? null : id;
  };

  const submitForm = async (e) => {
    e.preventDefault();
    setFormError(null);
    setSaving(true);
    try {
      const categoryId = await resolveCategoryId();
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        url: form.url.trim(),
        pinned: form.pinned,
        category: categoryId,
      };
      if (editingId) {
        await axios.patch(`${API_URL}/portfolio/info-items/${editingId}/`, payload);
      } else {
        await axios.post(`${API_URL}/portfolio/info-items/`, { ...payload, archived: false });
      }
      closeModal();
      await loadItems();
      notifyAttention();
    } catch (e) {
      const d = e.response?.data;
      let msg = e.message;
      if (typeof d === 'object' && d) {
        msg = Object.entries(d)
          .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
          .join('; ');
      } else if (typeof d === 'string') msg = d;
      setFormError(msg);
    } finally {
      setSaving(false);
    }
  };

  const setArchived = async (item, archived) => {
    try {
      await axios.patch(`${API_URL}/portfolio/info-items/${item.id}/`, { archived });
      await loadItems();
      notifyAttention();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const removeItem = async (item) => {
    if (!window.confirm(`Item “${item.title}” permanent verwijderen?`)) return;
    try {
      await axios.delete(`${API_URL}/portfolio/info-items/${item.id}/`);
      if (editingId === item.id) closeModal();
      await loadItems();
      notifyAttention();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  if (loading) {
    return <div className="shared-info-loading">Laden…</div>;
  }

  if (error && !items.length) {
    return <div className="shared-info-error shared-info">{error}</div>;
  }

  return (
    <div className="shared-info">
      {error ? <div className="shared-info-error">{error}</div> : null}

      <div className="shared-info-toolbar">
        <div className="shared-info-toolbar-left">
          <label className="shared-info-toolbar-field">
            <span className="shared-info-toolbar-label">Categorie</span>
            <select
              className="shared-info-toolbar-select"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              aria-label="Filter op categorie"
            >
              <option value="">Alle categorieën</option>
              <option value="__none__">— Geen categorie —</option>
              {categories.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            Toon gearchiveerde items
          </label>
        </div>
        <div className="shared-info-toolbar-right">
          <button
            type="button"
            className="shared-info-btn primary shared-info-toolbar-add"
            onClick={openAddModal}
          >
            Item toevoegen
          </button>
        </div>
      </div>

      {modalOpen ? (
        <div
          className="shared-info-modal-backdrop"
          role="presentation"
          onClick={closeModal}
        >
          <div
            className="shared-info-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="shared-info-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="shared-info-modal-header">
              <h2 id="shared-info-modal-title" className="shared-info-modal-title">
                {editingId ? 'Item bewerken' : 'Nieuw item'}
              </h2>
              <button
                type="button"
                className="shared-info-modal-close"
                onClick={closeModal}
                aria-label="Sluiten"
              >
                ×
              </button>
            </div>
            <div className="shared-info-modal-body">
              {formError ? <div className="shared-info-error">{formError}</div> : null}
              <form onSubmit={submitForm}>
                <div className="shared-info-form-grid">
                  <label className="full-width">
                    Titel *
                    <input
                      className="shared-info-input"
                      value={form.title}
                      onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                      required
                      maxLength={255}
                    />
                  </label>
                  <label className="full-width">
                    Omschrijving (optioneel, **vet**)
                    <textarea
                      value={form.description}
                      onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    />
                  </label>
                  <label className="full-width">
                    Link (https) *
                    <input
                      className="shared-info-input"
                      type="url"
                      value={form.url}
                      onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                      required
                      placeholder="https://…"
                    />
                  </label>
                  <label>
                    Categorie
                    <select
                      value={form.category}
                      onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                    >
                      <option value="">— Geen —</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Nieuwe categorie (optioneel)
                    <input
                      className="shared-info-input"
                      value={form.newCategoryName}
                      onChange={(e) => setForm((f) => ({ ...f, newCategoryName: e.target.value }))}
                      placeholder="Naam; overschrijft dropdown hierboven"
                    />
                  </label>
                  <label className="full-width" style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={form.pinned}
                      onChange={(e) => setForm((f) => ({ ...f, pinned: e.target.checked }))}
                    />
                    Vastgezet (bovenaan)
                  </label>
                </div>
                <div className="shared-info-form-actions">
                  <button type="submit" className="shared-info-btn primary" disabled={saving}>
                    {saving ? 'Opslaan…' : editingId ? 'Wijzigingen opslaan' : 'Toevoegen'}
                  </button>
                  <button type="button" className="shared-info-btn" onClick={closeModal}>
                    Annuleren
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      ) : null}

      <div className="shared-info-cards">
        {items.length === 0 ? (
          <div className="shared-info-empty">
            Geen items om te tonen. Pas het filter aan of klik op Item toevoegen.
          </div>
        ) : (
          items.map((item) => {
            const unseen = !item.seen;
            const showNew = !!item.show_updated_badge;
            return (
              <article
                key={item.id}
                className={`shared-info-card ${unseen ? 'unseen' : ''} ${item.pinned ? 'pinned' : ''} ${
                  item.archived ? 'archived' : ''
                }`}
              >
                <div className="shared-info-card-header">
                  <h3 className="shared-info-card-title">{item.title}</h3>
                  <div className="shared-info-badges">
                    {item.pinned ? <span className="shared-info-badge pinned">Vastgezet</span> : null}
                    {item.archived ? <span className="shared-info-badge archived">Archief</span> : null}
                    {showNew ? <span className="shared-info-badge new">Bijgewerkt</span> : null}
                  </div>
                </div>
                <div className="shared-info-meta">
                  {item.category_name ? <span>{item.category_name} · </span> : null}
                  {item.created_by_username ? <span>Door {item.created_by_username} · </span> : null}
                  <span suppressHydrationWarning>
                    {item.updated_at
                      ? `Bijgewerkt ${new Date(item.updated_at).toLocaleString()}`
                      : null}
                  </span>
                </div>
                {item.description ? (
                  <div className="shared-info-desc">{renderSimpleMarkdown(item.description)}</div>
                ) : null}
                <a
                  className="shared-info-link"
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {item.url} ↗
                </a>
                <div className="shared-info-actions">
                  <label>
                    <input
                      type="checkbox"
                      checked={!!item.seen}
                      onChange={(e) => patchSeen(item, e.target.checked)}
                    />
                    Gezien
                  </label>
                  <button type="button" className="shared-info-btn" onClick={() => startEdit(item)}>
                    Bewerken
                  </button>
                  {item.archived ? (
                    <button
                      type="button"
                      className="shared-info-btn"
                      onClick={() => setArchived(item, false)}
                    >
                      Herstel uit archief
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="shared-info-btn"
                      onClick={() => setArchived(item, true)}
                    >
                      Archiveren
                    </button>
                  )}
                  <button
                    type="button"
                    className="shared-info-btn shared-info-btn-danger"
                    onClick={() => removeItem(item)}
                  >
                    Verwijderen
                  </button>
                </div>
              </article>
            );
          })
        )}
      </div>
    </div>
  );
}

export default SharedInfo;
